/**
 * Shared Console filter contract (Lane 8).
 * Closed keys and discriminated window variants; callers never invent free-string sentences.
 * Reports keeps its exact legacy copy via the compatibility branch below.
 */

import {
  REPORT_TOO_MANY_SITES_COPY,
  REPORT_UNAVAILABLE_COPY,
  REPORT_WINDOW_HINT,
  reportDateLabel,
  reportFilterChipText,
  reportSitesChipValue,
  reportWindowCopy,
  resolveReportDateRange,
  type ReportDateSelection,
  type ReportScanLifecycleState,
  type ReportScanMethod,
  REPORT_LIFECYCLE_LABELS,
  REPORT_METHOD_LABELS,
} from "./report-scan-filters";
import { FREEFORM_PRESENCE_LABEL } from "./scan-list";
import { REPLAY_RETENTION_LAPSED } from "./command-replay";

/** Surfaces that may adopt the shared filter chrome (D-08). */
export const CONSOLE_FILTER_SURFACES = [
  "reports",
  "incidents",
  "live",
  "replay",
  "inbox",
] as const;
export type ConsoleFilterSurface = (typeof CONSOLE_FILTER_SURFACES)[number];

/** Allowlisted chip labels (D-02 / D-03). */
export const CONSOLE_FILTER_CHIP_LABELS = [
  "Client",
  "Site",
  "Sites",
  "Week",
  "Guard",
  "Officer",
  "Route",
  "Checkpoint",
  "Dates",
  "Status",
  "Severity",
  "Method",
  "Presence",
  "Review required",
] as const;
export type ConsoleFilterChipLabel = (typeof CONSOLE_FILTER_CHIP_LABELS)[number];

/** Noun keys for footer skeletons (D-05). Never a free string. */
export const CONSOLE_FILTER_NOUNS = ["scan", "incident", "capture"] as const;
export type ConsoleFilterNoun = (typeof CONSOLE_FILTER_NOUNS)[number];

export type ConsoleFilterFooterState = "all" | "capped" | "none";

/** Closed refusal vocabulary (D-07). */
export const CONSOLE_FILTER_REFUSALS = [
  "self_serve_floor",
  "retention_policy",
  "outside_retention",
  "stale_desk_clock",
  "permission",
  "support_session",
  "policy_off",
  "too_many_sites",
  "too_large",
  "unavailable",
] as const;
export type ConsoleFilterRefusal = (typeof CONSOLE_FILTER_REFUSALS)[number];

export type ConsoleFilterEmptyReason = "no_match" | "nothing_yet";

/**
 * Discriminated presentation windows (FG-06).
 * Only the scans variant is wired by Reports in S1; others are typed for later slices.
 */
export type ConsoleWindowVariant =
  | { readonly kind: "scans"; readonly limit: 500 }
  | { readonly kind: "live"; readonly limit: 50 }
  | { readonly kind: "incidents"; readonly limit: 200 }
  | {
      readonly kind: "replay";
      readonly samplesPerTape: 500;
      readonly maxTapes: 12;
    }
  | {
      readonly kind: "schedule_coverage";
      readonly timeZone: string;
      readonly weekStart: string;
    };

export const CONSOLE_WINDOW_SCANS = {
  kind: "scans",
  limit: 500,
} as const satisfies ConsoleWindowVariant;

export const CONSOLE_WINDOW_LIVE = {
  kind: "live",
  limit: 50,
} as const satisfies ConsoleWindowVariant;

export const CONSOLE_WINDOW_INCIDENTS = {
  kind: "incidents",
  limit: 200,
} as const satisfies ConsoleWindowVariant;

export const CONSOLE_WINDOW_REPLAY = {
  kind: "replay",
  samplesPerTape: 500,
  maxTapes: 12,
} as const satisfies ConsoleWindowVariant;

/** Quiet trailing strip notes. Closed keys only (no caller sentence). */
export type ConsoleFilterStripNote = "reports_print_hint";

export const CONSOLE_FILTER_STRIP_NOTES: Record<ConsoleFilterStripNote, string> = {
  reports_print_hint: "Need a PDF? Use your browser's Print on this page.",
};

/** About-this-window disclosure variants. Closed keys only. */
export type ConsoleWindowAboutVariant = "reports_scans";

export interface ConsoleFilterChip {
  readonly label: ConsoleFilterChipLabel;
  readonly value: string;
  /** Time chips are always present and have no remove control (D-03). */
  readonly removable: boolean;
}

export function consoleFilterChipText(
  label: ConsoleFilterChipLabel,
  value: string,
): string {
  if (label === "Client" || label === "Week") {
    return `${label} · ${value}`;
  }
  if (
    label === "Site" ||
    label === "Sites" ||
    label === "Guard" ||
    label === "Route" ||
    label === "Checkpoint" ||
    label === "Dates" ||
    label === "Method" ||
    label === "Presence" ||
    label === "Status" ||
    label === "Review required"
  ) {
    return reportFilterChipText(label, value);
  }
  return `${label} · ${value}`;
}

/** Surface display names for generic unavailable sentences (allowlisted). */
export const CONSOLE_FILTER_SURFACE_DISPLAY: Record<ConsoleFilterSurface, string> = {
  reports: "Reports",
  incidents: "Incidents",
  live: "Live",
  replay: "Replay",
  inbox: "Inbox",
};

const GENERIC_UNAVAILABLE_SUFFIX =
  " is unavailable right now. Check your access or try again.";

/** Window line copy for each discriminated variant (D-04 / D-17). */
export function consoleWindowLineCopy(variant: ConsoleWindowVariant): string {
  switch (variant.kind) {
    case "scans":
      return REPORT_WINDOW_HINT;
    case "live":
      return "Up to the most recent 50 matching captures. Older captures stay stored; this window does not delete them.";
    case "incidents":
      return "Up to the most recent 200 matching incidents. Older incidents stay stored; this window does not delete them.";
    case "replay":
      return "One day per tape, up to 500 samples per tape, up to twelve tapes. This window does not delete anything.";
    case "schedule_coverage":
      return `Site-local week in ${variant.timeZone}. Coverage reads live sources; these facts are not proof.`;
  }
}

/**
 * Reports compatibility footer/window copy (exact legacy sentences).
 * Preserves capped-at-limit semantics (shownCount >= limit); no +1 probe in S1.
 */
export function reportsResultCopy(input: {
  shownCount: number;
  siteFilter: string;
  guardFilter: string;
  dateSelection: ReportDateSelection;
  cappedSorted?: boolean;
}): { foot: string; pageHint: string; state: ConsoleFilterFooterState } {
  const copy = reportWindowCopy({
    shownCount: input.shownCount,
    limit: CONSOLE_WINDOW_SCANS.limit,
    siteFilter: input.siteFilter,
    guardFilter: input.guardFilter,
    dateSelection: input.dateSelection,
    ...(input.cappedSorted === undefined ? {} : { cappedSorted: input.cappedSorted }),
  });
  const state: ConsoleFilterFooterState =
    input.shownCount === 0
      ? "none"
      : input.shownCount >= CONSOLE_WINDOW_SCANS.limit
        ? "capped"
        : "all";
  return { ...copy, state };
}

/** Closed unavailable sentences. Reports self_serve_floor stays byte-identical. */
export function consoleUnavailableCopy(input: {
  surface: ConsoleFilterSurface;
  reason: ConsoleFilterRefusal;
  /** Replay outside-retention days from account policy (7|14|30). */
  retentionDays?: number;
  /**
   * Replay outside_retention contexts (FG-07).
   * `outside` = day never in window; `lapsed` = left while the desk was open (P-30 desk kept).
   */
  replayContext?: "outside" | "lapsed";
}): string {
  if (input.surface === "reports" && input.reason === "self_serve_floor") {
    return REPORT_UNAVAILABLE_COPY;
  }
  if (input.surface === "reports" && input.reason === "too_many_sites") {
    return REPORT_TOO_MANY_SITES_COPY;
  }
  if (input.surface === "replay" && input.reason === "outside_retention") {
    if (input.replayContext === "lapsed") {
      return REPLAY_RETENTION_LAPSED;
    }
    const days =
      typeof input.retentionDays === "number" && Number.isFinite(input.retentionDays)
        ? Math.trunc(input.retentionDays)
        : 30;
    return `That day is outside this account's Replay window of ${days} days. Pick a more recent day.`;
  }
  switch (input.reason) {
    case "self_serve_floor":
    case "retention_policy":
    case "permission":
    case "support_session":
    case "policy_off":
    case "too_many_sites":
    case "too_large":
    case "unavailable":
      return `${CONSOLE_FILTER_SURFACE_DISPLAY[input.surface]}${GENERIC_UNAVAILABLE_SUFFIX}`;
    case "outside_retention":
    case "stale_desk_clock":
      // stale_desk_clock stays heal/re-resolve on Replay; no Reload sentence in S4.
      return `${CONSOLE_FILTER_SURFACE_DISPLAY[input.surface]}${GENERIC_UNAVAILABLE_SUFFIX}`;
  }
}

export type ConsoleUnavailableAction = "request_retrieval" | "back_to_last_90_days";

export const CONSOLE_UNAVAILABLE_ACTION_LABELS: Record<ConsoleUnavailableAction, string> = {
  request_retrieval: "Request retrieval",
  back_to_last_90_days: "Back to last 90 days",
};

export const REPORTS_SELF_SERVE_UNAVAILABLE_ACTIONS = [
  "request_retrieval",
  "back_to_last_90_days",
] as const satisfies readonly ConsoleUnavailableAction[];

export const REPORTS_RETRIEVAL_MAILTO = "mailto:support@thesecops.app";

export type ReportsWindowAboutRow =
  | { readonly term: string; readonly detail: string; readonly href?: undefined }
  | {
      readonly term: string;
      readonly detail: string;
      readonly href: string;
      readonly linkLabel: string;
    };

/** Reports About-this-window rows (exact legacy markup copy). */
export const REPORTS_WINDOW_ABOUT_ROWS: readonly ReportsWindowAboutRow[] = [
  {
    term: "This view",
    detail:
      "Up to the 500 most recent scans that match your filters. This is a view and export window.",
  },
  {
    term: "Your window",
    detail: "You can reach the last 90 days of scans here on your own.",
  },
  {
    term: "Kept",
    detail: "Scan records stay stored for at least 12 months.",
  },
  {
    term: "Older scans",
    detail: "Ask SecOps support to retrieve older scans.",
    href: REPORTS_RETRIEVAL_MAILTO,
    linkLabel: "Ask SecOps support to retrieve older scans.",
  },
];

/**
 * Narrow kind-to-status adapter for SWAF envelope parity (O-5 / FC-05).
 * Does not rename ReportScanRead.kind; maps a completed Reports read for shared consumers.
 * Empty authorized windows become empty/no_match; denial/unavailable stay unavailable.
 */
export type ConsoleDeskReadStatus<Row> =
  | { readonly status: "ok"; readonly rows: readonly Row[]; readonly capped: boolean }
  | { readonly status: "empty"; readonly reason: ConsoleFilterEmptyReason }
  | { readonly status: "unavailable"; readonly reason: ConsoleFilterRefusal };

export function adaptReportKindToStatus<Row>(read: {
  readonly kind: "ok";
  readonly rows: readonly Row[];
  readonly capped: boolean;
} | {
  readonly kind: "unavailable";
  readonly reason: ConsoleFilterRefusal;
}): ConsoleDeskReadStatus<Row> {
  if (read.kind === "unavailable") {
    return { status: "unavailable", reason: read.reason };
  }
  if (read.rows.length === 0) {
    return { status: "empty", reason: "no_match" };
  }
  return { status: "ok", rows: read.rows, capped: read.capped };
}

/** Build Reports filter chips from current selections (compatibility helper). */
export function reportsFilterChips(input: {
  siteFilter?: string;
  siteNames?: readonly string[];
  guardFilter: string;
  routeName?: string | null;
  checkpointName?: string | null;
  methods?: readonly ReportScanMethod[];
  presenceAdhocOnly?: boolean;
  lifecycleStates?: readonly ReportScanLifecycleState[];
  reviewRequiredOnly?: boolean;
  dateSelection: ReportDateSelection;
}): ConsoleFilterChip[] {
  const chips: ConsoleFilterChip[] = [];
  const siteNames =
    input.siteNames !== undefined
      ? input.siteNames
      : input.siteFilter && input.siteFilter.length > 0
        ? [input.siteFilter]
        : [];
  const sitesValue = reportSitesChipValue(siteNames);
  if (sitesValue.length > 0) {
    chips.push({
      label: siteNames.length > 1 ? "Sites" : "Site",
      value: sitesValue,
      removable: true,
    });
  }
  if (input.guardFilter.length > 0) {
    chips.push({ label: "Guard", value: input.guardFilter, removable: true });
  }
  if (input.routeName && input.routeName.length > 0) {
    chips.push({ label: "Route", value: input.routeName, removable: true });
  }
  if (input.checkpointName && input.checkpointName.length > 0) {
    chips.push({
      label: "Checkpoint",
      value: input.checkpointName,
      removable: true,
    });
  }
  if (input.methods !== undefined && input.methods.length > 0) {
    chips.push({
      label: "Method",
      value: input.methods.map((method) => REPORT_METHOD_LABELS[method]).join(", "),
      removable: true,
    });
  }
  if (input.presenceAdhocOnly === true) {
    chips.push({
      label: "Presence",
      value: FREEFORM_PRESENCE_LABEL,
      removable: true,
    });
  }
  if (input.lifecycleStates !== undefined && input.lifecycleStates.length > 0) {
    chips.push({
      label: "Status",
      value: input.lifecycleStates
        .map((state) => REPORT_LIFECYCLE_LABELS[state])
        .join(", "),
      removable: true,
    });
  }
  if (input.reviewRequiredOnly === true) {
    chips.push({ label: "Review required", value: "On", removable: true });
  }
  chips.push({
    label: "Dates",
    value: reportDateLabel(input.dateSelection),
    removable: false,
  });
  return chips;
}

/**
 * Incidents footer/window copy (D-05). No export clause.
 * Cap honesty: shownCount >= 200 is capped (no +1 probe in S2).
 */
export function incidentsResultCopy(input: {
  shownCount: number;
  siteFilter: string;
  statusFilter: string;
  severityFilter: string;
}): { foot: string; pageHint: string; state: ConsoleFilterFooterState } {
  const pageHint = consoleWindowLineCopy(CONSOLE_WINDOW_INCIDENTS);
  const scope: string[] = [];
  if (input.siteFilter.length > 0) scope.push(`site ${input.siteFilter}`);
  if (input.statusFilter.length > 0) scope.push(`status ${input.statusFilter}`);
  if (input.severityFilter.length > 0) scope.push(`severity ${input.severityFilter}`);
  const scopeClause = scope.length > 0 ? ` matching ${scope.join(" and ")}` : "";

  if (input.shownCount === 0) {
    return {
      pageHint,
      state: "none",
      foot: "No incidents match those filters in this window.",
    };
  }
  if (input.shownCount >= CONSOLE_WINDOW_INCIDENTS.limit) {
    return {
      pageHint,
      state: "capped",
      foot: `Showing the most recent ${CONSOLE_WINDOW_INCIDENTS.limit} incidents${scopeClause}. Older matching incidents stay stored and are outside this view. Narrow the filters to reach them.`,
    };
  }
  const noun = input.shownCount === 1 ? "1 incident" : `${input.shownCount} incidents`;
  return {
    pageHint,
    state: "all",
    foot: `Showing all ${noun}${scopeClause}.`,
  };
}

/** Build Incidents filter chips from current selections. */
export function incidentsFilterChips(input: {
  siteFilter: string;
  statusFilter: string;
  severityFilter: string;
}): ConsoleFilterChip[] {
  const chips: ConsoleFilterChip[] = [];
  if (input.siteFilter.length > 0) {
    chips.push({ label: "Site", value: input.siteFilter, removable: true });
  }
  if (input.statusFilter.length > 0) {
    chips.push({ label: "Status", value: input.statusFilter, removable: true });
  }
  if (input.severityFilter.length > 0) {
    chips.push({ label: "Severity", value: input.severityFilter, removable: true });
  }
  return chips;
}

/** Live activity time presets (FG-01). No All time. */
export const LIVE_TIME_PRESETS = [
  { value: "last_hour", label: "Last hour" },
  { value: "last_8_hours", label: "Last 8 hours" },
  { value: "today", label: "Today" },
] as const;

export type LiveTimePreset = (typeof LIVE_TIME_PRESETS)[number]["value"];

/** Confirmed Live default: matches Command Center "last hour" badge language. */
export const DEFAULT_LIVE_TIME_PRESET: LiveTimePreset = "last_hour";

export function liveTimePresetLabel(preset: LiveTimePreset): string {
  const row = LIVE_TIME_PRESETS.find((entry) => entry.value === preset);
  return row?.label ?? "Last hour";
}

export function isLiveTimePreset(value: string): value is LiveTimePreset {
  return LIVE_TIME_PRESETS.some((entry) => entry.value === value);
}

/**
 * Resolve Live host bounds for `server_received_at`.
 * Relative presets are wall-clock from `now`; Today starts at local midnight in `timeZone`.
 */
export function resolveLiveTimeBounds(
  preset: LiveTimePreset,
  timeZone: string,
  now: Date = new Date(),
): { readonly fromIso: string; readonly toIso: string } {
  const toIso = now.toISOString();
  if (preset === "last_hour") {
    return {
      fromIso: new Date(now.getTime() - 60 * 60 * 1000).toISOString(),
      toIso,
    };
  }
  if (preset === "last_8_hours") {
    return {
      fromIso: new Date(now.getTime() - 8 * 60 * 60 * 1000).toISOString(),
      toIso,
    };
  }
  const resolution = resolveReportDateRange({ preset: "today" }, timeZone, now);
  if (resolution.kind === "ok") {
    return { fromIso: resolution.range.fromIso, toIso };
  }
  // Stale clock or other refusal: still bound to UTC midnight of the desk instant.
  const utcDay = now.toISOString().slice(0, 10);
  return { fromIso: `${utcDay}T00:00:00.000Z`, toIso };
}

function liveTimeScopePhrase(preset: LiveTimePreset): string {
  switch (preset) {
    case "last_hour":
      return "in the last hour";
    case "last_8_hours":
      return "in the last 8 hours";
    case "today":
      return "today";
  }
}

/**
 * Live activity footer/window copy (D-05 / FC-12). Noun is always "capture(s)".
 * Cap honesty: shownCount >= 50 is capped (no +1 probe in S3).
 */
export function liveResultCopy(input: {
  shownCount: number;
  siteFilter: string;
  guardFilter: string;
  timePreset: LiveTimePreset;
}): { foot: string; pageHint: string; state: ConsoleFilterFooterState } {
  const pageHint = consoleWindowLineCopy(CONSOLE_WINDOW_LIVE);
  const scope: string[] = [];
  if (input.siteFilter.length > 0) scope.push(`site ${input.siteFilter}`);
  if (input.guardFilter.length > 0) scope.push(`guard ${input.guardFilter}`);
  const matching =
    scope.length > 0
      ? ` matching ${scope.join(" and ")} ${liveTimeScopePhrase(input.timePreset)}`
      : ` ${liveTimeScopePhrase(input.timePreset)}`;

  if (input.shownCount === 0) {
    return {
      pageHint,
      state: "none",
      foot: "No captures match those filters in this window.",
    };
  }
  if (input.shownCount >= CONSOLE_WINDOW_LIVE.limit) {
    return {
      pageHint,
      state: "capped",
      foot: `Showing the most recent ${CONSOLE_WINDOW_LIVE.limit} captures${matching}. Older matching captures stay stored and are outside this view. Narrow the filters to reach them.`,
    };
  }
  const noun = input.shownCount === 1 ? "1 capture" : `${input.shownCount} captures`;
  return {
    pageHint,
    state: "all",
    foot: `Showing all ${noun}${matching}.`,
  };
}

/** Build Live filter chips. Dates is always present and not removable (no All time). */
export function liveFilterChips(input: {
  siteFilter: string;
  guardFilter: string;
  timePreset: LiveTimePreset;
}): ConsoleFilterChip[] {
  const chips: ConsoleFilterChip[] = [];
  if (input.siteFilter.length > 0) {
    chips.push({ label: "Site", value: input.siteFilter, removable: true });
  }
  if (input.guardFilter.length > 0) {
    chips.push({ label: "Guard", value: input.guardFilter, removable: true });
  }
  chips.push({
    label: "Dates",
    value: liveTimePresetLabel(input.timePreset),
    removable: false,
  });
  return chips;
}

/**
 * Inbox source caps (must match host readers).
 * Critical/alerts 50, Action/Feed 50, Boards 100, Messages threads 100.
 */
export const INBOX_SOURCE_LIMITS = {
  critical: 50,
  action: 50,
  boards: 100,
  messages: 100,
} as const;

export type InboxFooterSegment = keyof typeof INBOX_SOURCE_LIMITS;

/** Closed Inbox footer nouns (D-05 / FC-17). No WindowLine on Inbox. */
export const INBOX_FOOTER_NOUNS = {
  critical: "Critical items",
  action: "Action items",
  boards: "announcement boards",
  messages: "message threads",
} as const satisfies Record<InboxFooterSegment, string>;

/**
 * Inbox ResultFooter copy. Never claims "all" when shownCount hits the source cap.
 * Empty Critical keeps the shipped sentence; other empties stay segment-local.
 */
export function inboxResultCopy(input: {
  segment: InboxFooterSegment;
  shownCount: number;
}): { foot: string; state: ConsoleFilterFooterState } {
  const limit = INBOX_SOURCE_LIMITS[input.segment];
  const noun = INBOX_FOOTER_NOUNS[input.segment];

  if (input.shownCount === 0) {
    if (input.segment === "critical") {
      return { state: "none", foot: "No open Critical items." };
    }
    return {
      state: "none",
      foot: `No ${noun} in this segment.`,
    };
  }
  if (input.shownCount >= limit) {
    return {
      state: "capped",
      foot: `Showing the most recent ${limit} ${noun} available in this segment. Older matching items stay stored; this segment does not delete them.`,
    };
  }
  const countLabel =
    input.shownCount === 1
      ? input.segment === "boards"
        ? "1 announcement board"
        : input.segment === "messages"
          ? "1 message thread"
          : input.segment === "critical"
            ? "1 Critical item"
            : "1 Action item"
      : `${input.shownCount} ${noun}`;
  return {
    state: "all",
    foot: `Showing all ${countLabel} available in this segment.`,
  };
}

/** Map Inbox section kind to footer segment. */
export function inboxFooterSegmentForKind(
  kind: "sos" | "feed_ack" | "board" | "message",
): InboxFooterSegment {
  switch (kind) {
    case "sos":
      return "critical";
    case "feed_ack":
      return "action";
    case "board":
      return "boards";
    case "message":
      return "messages";
  }
}
